import React, { useEffect, useState } from 'react';
import MainLayout from '../components/MainLayout';
import FloorPlanCanvas from '../components/floorplan/FloorPlanCanvas';
import SimpleTableTile from '../components/SimpleTableTile';
import PagerTile from '../components/PagerTile';
import LayoutModeToggle from '../components/LayoutModeToggle';
import ActiveStaffBadge from '../components/ActiveStaffBadge';
import { FinalizeOrderCartItemModal } from '../components/modals/FinalizeOrderCartItemModal';
import PeopleCountModal from '../components/modals/PeopleCountModal';
import DigitalReceiptModal from '../components/modals/DigitalReceiptModal';
import { apiUrls } from '../utils/apiUrls';
import { callAPI } from '../utils/apiUtils';
import useTableOrdering from '../hooks/useTableOrdering';
import '../styles/tableFormat.css';
import '../styles/floorPlan.css';

const SPECIAL_TABS = ["person-tab", "tabs-tab", "takeaway-tab", "pager-tab"];

const groupByZone = (tables) => {
  const grouped = {};
  tables.forEach((table) => {
    const zoneId = table.zone_id;
    if (!grouped[zoneId]) grouped[zoneId] = [];
    grouped[zoneId].push(table);
  });
  return grouped;
};

/**
 * The visual-floor-plan alternative to DashboardPage — same table-selection behavior (shared via
 * useTableOrdering), but real seating zones render as the actual saved floor plan (positions,
 * shapes, colors, chairs) instead of a flat grid. The three non-seating tabs (Person/Tabs/
 * Takeaway) render exactly as they do in the grid view, via the same SimpleTableTile component.
 */
const FloorPlanDashboardPage = () => {
  const [zones, setZones] = useState([]);
  const [tablesByZoneId, setTablesByZoneId] = useState({});
  const [elementsByZoneId, setElementsByZoneId] = useState({});
  const [activeTabId, setActiveTabId] = useState(null);
  const [loadingFloorPlan, setLoadingFloorPlan] = useState(true);
  // Per-device preference, set in Settings - never sent to the server, so it's read straight from
  // localStorage rather than from any of the restaurant-wide state loaded above. The custom event
  // keeps this in sync if Settings is flipped while this page stays mounted (same pattern as
  // LayoutModeToggle.js's layoutModeChanged event).
  const [hideChairs, setHideChairs] = useState(localStorage.getItem('hide_floor_plan_chairs') === '1');
  useEffect(() => {
    const onChange = () => setHideChairs(localStorage.getItem('hide_floor_plan_chairs') === '1');
    window.addEventListener('floorPlanChairsVisibilityChanged', onChange);
    return () => window.removeEventListener('floorPlanChairsVisibilityChanged', onChange);
  }, []);

  const {
    selectedTables,
    seatingTabs,
    multiSelect,
    occupiedTables,
    cartModal,
    setCartModal,
    peopleModal,
    setPeopleModal,
    receiptModal,
    closeReceiptModal,
    handleMultiSelect,
    handleTableSelect,
    handlePagerTap,
    handlePeopleModalSuccess,
    handlePeopleModalNext,
    handleInfoClick,
    handleViewReceipt,
    printKitchenDocket,
    getElapsed,
    filteredTableData,
    handleCheckoutClick,
    anySelected,
    handleNext,
  } = useTableOrdering();

  useEffect(() => {
    async function fetchFloorPlan() {
      try {
        const res = await callAPI(apiUrls.FLOOR_PLAN_BOOTSTRAP, {}, 'GET');
        const data = res.data?.data || {};
        setZones(data.zones || []);
        setTablesByZoneId(groupByZone(data.tables || []));
        setElementsByZoneId(groupByZone(data.elements || []));
      } catch (err) {
        console.error('Failed to load floor plan', err);
      } finally {
        setLoadingFloorPlan(false);
      }
    }
    fetchFloorPlan();
  }, []);

  const specialTabsInUse = seatingTabs.filter((tab) => SPECIAL_TABS.includes(tab.id));

  // Zones and seatingTabs load from two independent fetches that can resolve in either order.
  // Falls back to the first special tab (Person/Tabs/Takeaway) when a restaurant has none of the
  // physical seating zones this page defaults to, so the view is never left blank with a tab bar
  // but nothing selected - but only once the floor-plan fetch has actually finished, so a special
  // tab that merely resolved first doesn't "win" over a real zone that's still loading.
  useEffect(() => {
    if (activeTabId || loadingFloorPlan) return;
    if (zones.length > 0) {
      setActiveTabId(zones[0].value);
    } else if (specialTabsInUse.length > 0) {
      setActiveTabId(specialTabsInUse[0].id);
    }
  }, [zones, specialTabsInUse, activeTabId, loadingFloorPlan]);

  const activeZone = zones.find((z) => z.value === activeTabId);
  const activeSpecialTab = specialTabsInUse.find((tab) => tab.id === activeTabId);

  const renderSpecialTabTiles = (tabId) => {
    const list = filteredTableData[tabId] || [];
    return list.map((table) => {
      const isSelected = (selectedTables?.[tabId] || []).includes(table.id);
      const info = occupiedTables[table.id];

      if (tabId === 'pager-tab') {
        return (
          <PagerTile
            key={table.id}
            table={table}
            isOccupied={!!info}
            info={info}
            onTap={handlePagerTap}
            onViewReceipt={handleViewReceipt}
          />
        );
      }

      return (
        <SimpleTableTile
          key={table.id}
          table={table}
          tabId={tabId}
          isSelected={isSelected}
          isOccupied={!!info}
          info={info}
          isSpecialTab
          onSelect={() => handleTableSelect(tabId, table.id, table.name || `${table.tableNumber}`)}
          onInfoClick={handleInfoClick}
          onCheckout={() => handleCheckoutClick(tabId, table.id)}
          onPrintKitchenDocket={(type) => printKitchenDocket(table.id, type)}
          getElapsed={getElapsed}
        />
      );
    });
  };

  const footerButtons = (
    <button
      className="btn iconBtn btnPrimary"
      onClick={handleNext}
      disabled={!anySelected}
    >
      <span>Next</span>
      <svg width="20" height="20" className="btnIcon">
        <use href="./images/sprite.svg#iconNextArrow" />
      </svg>
    </button>
  );

  return (
    <MainLayout
      footerButtons={footerButtons}
      footerCenter={(
        <div className="d-flex align-items-center gap-3">
          <LayoutModeToggle id="dashboardLayoutModeToggle" />
          <ActiveStaffBadge />
        </div>
      )}
    >
      <div className="pageContent floorPlanPage floorPlanOrderView">
        <div className="container">
          {loadingFloorPlan ? (
            <p>Loading floor plan...</p>
          ) : (
            <>
              <div className="zoneTabBar">
                <div className="zoneTabList">
                  {zones.map((zone) => (
                    <div
                      key={zone.id}
                      className={`zoneTab${activeTabId === zone.value ? ' active' : ''}`}
                    >
                      <button type="button" className="zoneTabButton" onClick={() => setActiveTabId(zone.value)}>
                        <span className="zoneTabDot" style={{ background: zone.color || 'var(--primary, #e94059)' }} />
                        {zone.name}
                      </button>
                    </div>
                  ))}
                  {specialTabsInUse.map((tab) => (
                    <div
                      key={tab.id}
                      className={`zoneTab${activeTabId === tab.id ? ' active' : ''}`}
                    >
                      <button type="button" className="zoneTabButton" onClick={() => setActiveTabId(tab.id)}>
                        <span className="zoneTabDot" style={{ background: tab.color || 'rgba(255, 255, 255, 0.35)' }} />
                        {tab.label}
                      </button>
                    </div>
                  ))}
                </div>

                {activeZone && (
                  <div className="form-check form-switch mb-0 multiSelectTrigger ms-auto">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`multiSelect-${activeZone.value}`}
                      checked={!!multiSelect[activeZone.value]}
                      onChange={() => handleMultiSelect(activeZone.value)}
                    />
                    <label className="form-check-label" htmlFor={`multiSelect-${activeZone.value}`}>
                      Multiple Select
                    </label>
                  </div>
                )}
              </div>

              {activeZone && (
                <FloorPlanCanvas
                  tables={tablesByZoneId[activeZone.id] || []}
                  elements={elementsByZoneId[activeZone.id] || []}
                  selectedTableIds={[]}
                  selectedElementIds={[]}
                  onSelectTable={(tableId) => {
                    const table = (tablesByZoneId[activeZone.id] || []).find((t) => t.id === tableId);
                    handleTableSelect(activeZone.value, tableId, table?.name);
                  }}
                  onSelectElement={() => {}}
                  onDeselect={() => {}}
                  onTableChange={() => {}}
                  onElementChange={() => {}}
                  readOnly
                  hideChairs={hideChairs}
                  occupiedTables={occupiedTables}
                  multiSelectedIds={selectedTables?.[activeZone.value] || []}
                  onCheckout={(tableId) => handleCheckoutClick(activeZone.value, tableId)}
                  onPrintMains={(tableId) => printKitchenDocket(tableId, 'c')}
                  onPrintDessert={(tableId) => printKitchenDocket(tableId, 'd')}
                  onInfoClick={(tableId, tableName) => handleInfoClick(tableId, tableName)}
                  getElapsed={getElapsed}
                />
              )}

              {activeSpecialTab && (
                <div
                  className={`d-flex flex-wrap justify-content-center mx-auto col-10${activeSpecialTab.id === 'pager-tab' ? ' pagerGrid' : ' gap-5'}`}
                >
                  {renderSpecialTabTiles(activeSpecialTab.id).length > 0
                    ? renderSpecialTabTiles(activeSpecialTab.id)
                    : <p>No tables found</p>}
                </div>
              )}
            </>
          )}
        </div>

        <FinalizeOrderCartItemModal
          isOpen={cartModal.open}
          tableId={cartModal.tableId}
          tableName={cartModal.tableName}
          onClose={() => setCartModal({ open: false })}
        />
        <PeopleCountModal
          isOpen={peopleModal.open}
          tableId={peopleModal.tableId}
          tableName={peopleModal.tableName}
          onClose={() =>
            setPeopleModal({ open: false, tabId: null, tableId: null, tableName: '' })
          }
          onSuccess={handlePeopleModalSuccess}
          onNext={handlePeopleModalNext}
        />
        <DigitalReceiptModal
          isOpen={receiptModal.open}
          orderId={receiptModal.orderId}
          tableId={receiptModal.tableId}
          paymentMethod={receiptModal.paymentMethod}
          summary={receiptModal.summary}
          tableLabel={receiptModal.tableLabel}
          serverName={receiptModal.serverName}
          onClose={closeReceiptModal}
        />
      </div>
    </MainLayout>
  );
};

export default FloorPlanDashboardPage;
